import React, { Suspense, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import DashboardPageContainer from '../Containers/Dashboard/DashboardPageContainer';
import { DemeterFeatureType, DemeterPermissionType, DemeterUserType } from '../Generated/Raven-Demeter';
import { useApplicationSelector } from '../Redux/ReduxStore';
import { selectUserType } from '../Redux/Slices/UserSlice';
import PageLoadingSpinner from '../Refactor/Components/LoadingSpinner/PageLoadingSpinner';
import OktaLoginCallback from '../Refactor/Core/Okta/OktaLoginCallback';
import AdministrationPage from '../Refactor/Pages/Administration/AdministrationPage';
import CompaniesPage from '../Refactor/Pages/Administration/Companies/CompaniesPage';
import CompanyPage from '../Refactor/Pages/Administration/Companies/CompanyPage';
// eslint-disable-next-line max-len
import CompanyUsersPage from '../Refactor/Pages/Administration/Companies/CompanyUsersPage';
import MarketIndicatorFactorPage from '../Refactor/Pages/Administration/MarketIndicatorsManagement/MarketIndicatorFactor/MarketIndicatorFactorPage';
import MarketIndicatorsMangementPage from '../Refactor/Pages/Administration/MarketIndicatorsManagement/MarketIndicatorsManagementPage';
import PositionsPage from '../Refactor/Pages/Administration/Positions/PositionsPage';
import UserPage from '../Refactor/Pages/Administration/Users/UserPage';
import UsersImportPage from '../Refactor/Pages/Administration/Users/UsersImportPage';
import UsersPage from '../Refactor/Pages/Administration/Users/UsersPage';
import ArtificialIntelligenceNewsRatingPage from '../Refactor/Pages/ArtificialIntelligence/ArtificialIntelligencePage';
import BalanceTablePage from '../Refactor/Pages/BalanceTable/BalanceTablePage';
import CalculatorsPage from '../Refactor/Pages/Calculators/CalculatorsPage';
import CalculatorsValorizationListPage from '../Refactor/Pages/Calculators/ValorizationCalculator/CalculatorsValorizationListPage';
import CancelTrialPage from '../Refactor/Pages/CancelTrial/CancelTrialPage';
import ChartBuilderCreateChartPage from '../Refactor/Pages/ChartBuilder/ChartBuilderCreateChartPage';
import ChartBuilderPage from '../Refactor/Pages/ChartBuilder/ChartBuilderPage';
import DisclaimerPage from '../Refactor/Pages/Disclaimer/DisclaimerPage';
import DomesticsPage from '../Refactor/Pages/Domestics/DomesticsPage';
import EmailSubscriptionsPage from '../Refactor/Pages/EmailSubscriptions/EmailSubscriptionsPage';
import ExportsPage from '../Refactor/Pages/Exports/ExportsPage';
import FeatureFlagsPage from '../Refactor/Pages/FeatureFlags/FeatureFlagsPage';
import FrequentlyAskedQuestionsPage from '../Refactor/Pages/FrequentlyAskedQuestions/FrequentlyAskedQuestionsPage';
import FuturesRefactorPage from '../Refactor/Pages/Futures/FuturesPage';
import HedgeMonitorProPage from '../Refactor/Pages/HedgeMonitorPro/HedgeMonitorProPage';
import ImportsPage from '../Refactor/Pages/Imports/ImportsPage';
import MarketIndicatorsPage from '../Refactor/Pages/MarketIndicators/MarketIndicatorsPage';
import MarketIntelligenceReportsPage from '../Refactor/Pages/MarketIntelligenceReports/MarketIntelligenceReportsPage';
import NotFoundPage from '../Refactor/Pages/NotFound/NotFoundPage';
import OutlookAndReportsPage from '../Refactor/Pages/OutlookAndReports/OutlookAndReportsPage';
import PhysicalPricesRefactorPage from '../Refactor/Pages/PhysicalPrices/PhysicalPricesPage';
import PresentationTemplateEditPage from '../Refactor/Pages/PresentationTemplates/PresentationTemplateEditPage';
import PresentationTemplatesPage from '../Refactor/Pages/PresentationTemplates/PresentationTemplatesPage';
import PrivacyPolicyPage from '../Refactor/Pages/PrivacyPolicy/PrivacyPolicyPage';
import ProductionPage from '../Refactor/Pages/Productions/ProductionPage';
import RegistrationActivatePage from '../Refactor/Pages/Registration/RegistrationActivatePage';
import RegistrationSelfPage from '../Refactor/Pages/Registration/RegistrationSelfPage';
import RegistrationSelfSuccessPage from '../Refactor/Pages/Registration/RegistrationSelfSuccessPage';
import RiskPage from '../Refactor/Pages/Risk/RiskPage';
import SessionExpirationPage from '../Refactor/Pages/Session/SessionExpirationPage';
import StocksPage from '../Refactor/Pages/Stocks/StocksPage';
import UserDataChartPage from '../Refactor/Pages/UserData/UserDataChartPage';
import UserDataItemsPage from '../Refactor/Pages/UserData/UserDataItemsPage';
import UserDataPage from '../Refactor/Pages/UserData/UserDataPage';
import useFeatureFlag from '../Refactor/Services/FeatureFlags/useFeatureFlagHook';
import permissionsService from '../Refactor/Services/Permissions/PermissionsService';
import MonoHomePage from './Mono/MonoHomePage';
import NavigationRoutes, { NavigationRoutesType, PublicRoutes } from './NavigationRoutes';
import Logout from './Standalone/Logout';
import StandaloneHomePage from './Standalone/StandaloneHomePage';

interface RouterRoutesProps {
    secureRouteContainer: React.ReactNode;
    isMono: boolean;
}

const MonoRouteSuffix = '/*';
const OriginalNavigationRoutes = { ...NavigationRoutes };

interface IProtectedElementProps {
    children: JSX.Element;
    featureType: DemeterFeatureType;
    permissionTypes?: DemeterPermissionType | DemeterPermissionType[];
}

const ProtectedElement: React.FC<IProtectedElementProps> = (props: IProtectedElementProps) => {
    let canAccessPage = useFeatureFlag(props.featureType);

    if (props.permissionTypes) {
        if (Array.isArray(props.permissionTypes)) {
            canAccessPage = canAccessPage && props.permissionTypes.some((x) => permissionsService.getPermission(x));
        } else {
            canAccessPage = canAccessPage && permissionsService.getPermission(props.permissionTypes);
        }
    }

    return canAccessPage ? props.children : <Navigate to={NavigationRoutes.Dashboard as string} />;
};

interface IAdministratorElementProps {
    children: JSX.Element;
}

const AdminstratorProtectedElement: React.FC<IAdministratorElementProps> = (props: IAdministratorElementProps) => {
    const currentUserType = useApplicationSelector(selectUserType);
    return currentUserType === DemeterUserType.Administrator ? props.children : <Navigate to={NavigationRoutes.Dashboard as string} />;
};

const BusinessOwnerProtectedElement: React.FC<IAdministratorElementProps> = (props: IAdministratorElementProps) => {
    const currentUserType = useApplicationSelector(selectUserType);
    return currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner ? (
        props.children
    ) : (
        <Navigate to={NavigationRoutes.Dashboard as string} />
    );
};

const RouterRoutes: React.FC<RouterRoutesProps> = (props: RouterRoutesProps) => {
    // Translate the route to a regular React route or a Mono route.
    const [routes] = useState(() => {
        let translatedRoutes: NavigationRoutesType = {};

        if (props.isMono) {
            const applicationContainerRoute = OriginalNavigationRoutes.ApplicationContainer;

            Object.entries(OriginalNavigationRoutes).forEach(([key, value]) => {
                translatedRoutes[key] = `${value}${MonoRouteSuffix}`;
                (NavigationRoutes as NavigationRoutesType)[key] = PublicRoutes[key] ? `/${value}` : `/${applicationContainerRoute}/${value}`;
            });

            translatedRoutes.ApplicationContainer = `/${OriginalNavigationRoutes.ApplicationContainer}`;
        } else {
            translatedRoutes = { ...OriginalNavigationRoutes, ...{ Home: '/' } };

            const applicationContainerRoute = OriginalNavigationRoutes.ApplicationContainer;
            Object.entries(OriginalNavigationRoutes).forEach(([key, value]) => {
                (NavigationRoutes as NavigationRoutesType)[key] = PublicRoutes[key] ? `/${value}` : `/${applicationContainerRoute}/${value}`;
            });
        }
        return translatedRoutes;
    });

    return (
        <Suspense fallback={<PageLoadingSpinner />}>
            <Routes>
                {!props.isMono && (
                    <>
                        <Route path={routes.Home} element={<StandaloneHomePage />} />
                        <Route path={routes.Logout} element={<Logout />} />
                        <Route path={routes.LoginCallback} element={<OktaLoginCallback />} />
                        <Route path={routes.RegistrationActivate} element={<RegistrationActivatePage />} />
                        <Route path={routes.RegistrationSelf} element={<RegistrationSelfPage />} />
                        <Route path={routes.RegistrationSelfSuccess} element={<RegistrationSelfSuccessPage />} />
                        <Route path={routes.SessionExpiration} element={<SessionExpirationPage />} />
                        <Route path="*" element={<NotFoundPage />} />
                    </>
                )}
                {props.isMono && (
                    <>
                        <Route path={routes.Home} element={<MonoHomePage />} />
                        <Route path={routes.SessionExpiration} element={<SessionExpirationPage />} />
                    </>
                )}
                <Route path={routes.ApplicationContainer} element={props.secureRouteContainer}>
                    <Route path={routes.Dashboard} element={<DashboardPageContainer />} />
                    <Route path={routes.Futures} element={<FuturesRefactorPage />} />
                    <Route path={routes.HedgeMonitorPro} element={<HedgeMonitorProPage />} />
                    <Route path={routes.PhysicalPrices} element={<PhysicalPricesRefactorPage />} />
                    <Route path={routes.Production} element={<ProductionPage />} />
                    <Route path={routes.Imports} element={<ImportsPage />} />
                    <Route path={routes.Exports} element={<ExportsPage />} />
                    <Route path={routes.Stocks} element={<StocksPage />} />
                    <Route path={routes.Domestics} element={<DomesticsPage />} />
                    <Route path={routes.BalanceTable} element={<BalanceTablePage />} />
                    <Route
                        path={routes.MarketIndicators}
                        element={
                            <ProtectedElement
                                featureType={DemeterFeatureType.MarketIndicators}
                                permissionTypes={[
                                    DemeterPermissionType.MarketIndicators,
                                    DemeterPermissionType.MarketIndicatorAdministrator,
                                    DemeterPermissionType.MarketIndicatorDeveloper,
                                    DemeterPermissionType.MarketIndicatorSubjectMatterExpert,
                                    DemeterPermissionType.MarketIndicatorCompliance,
                                ]}
                            >
                                <MarketIndicatorsPage />
                            </ProtectedElement>
                        }
                    />
                    <Route path={routes.Calculators} element={<CalculatorsPage />} />
                    <Route
                        path={routes.ArtificialIntelligenceNewsRating}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.ArtificialIntelligenceNewsRating}>
                                    <ArtificialIntelligenceNewsRatingPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />

                    <Route
                        path={routes.ChartBuilderList}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.ChartBuilder} permissionTypes={[DemeterPermissionType.ChartBuilder]}>
                                <ChartBuilderPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.ChartBuilder}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.ChartBuilder} permissionTypes={[DemeterPermissionType.ChartBuilder]}>
                                <ChartBuilderCreateChartPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.ChartBuilderEdit}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.ChartBuilder} permissionTypes={[DemeterPermissionType.ChartBuilder]}>
                                <ChartBuilderCreateChartPage />
                            </ProtectedElement>
                        }
                    />

                    <Route
                        path={routes.CalculatorsValorizationList}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.Valorization} permissionTypes={[DemeterPermissionType.Valorization]}>
                                <CalculatorsValorizationListPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.Risk}
                        element={
                            <ProtectedElement
                                featureType={
                                    DemeterFeatureType.PositionsCalculator || DemeterFeatureType.BudgetCalculator || DemeterFeatureType.FarmerMarginCalculator
                                }
                                permissionTypes={[
                                    DemeterPermissionType.PositionsCalculator,
                                    DemeterPermissionType.BudgetCalculator,
                                    DemeterPermissionType.FarmerMarginCalculator,
                                ]}
                            >
                                <RiskPage />
                            </ProtectedElement>
                        }
                    />
                    <Route path={routes.UserData} element={<UserDataPage />} />
                    <Route path={routes.UserDataItems} element={<UserDataItemsPage />} />
                    <Route path={routes.UserDataChart} element={<UserDataChartPage />} />
                    <Route
                        path={routes.PresentationTemplates}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.PresentationTemplates}>
                                <PresentationTemplatesPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.PresentationTemplatesEdit}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.PresentationTemplates}>
                                <PresentationTemplateEditPage />
                            </ProtectedElement>
                        }
                    />
                    <Route path={routes.EmailSubscriptions} element={<EmailSubscriptionsPage />} />
                    <Route
                        path={routes.Administration}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.Administration}>
                                <AdministrationPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.Companies}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.Companies}>
                                    <CompaniesPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.CompaniesAdd}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.Companies}>
                                    <CompanyPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.CompaniesEdit}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.Companies}>
                                    <CompanyPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.Positions}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement
                                    featureType={DemeterFeatureType.FarmerMarginCalculator}
                                    permissionTypes={DemeterPermissionType.FarmerMarginAdministrator}
                                >
                                    <PositionsPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.CompaniesUsers}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.Users}>
                                <CompanyUsersPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.Users}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.Users}>
                                <UsersPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.UsersAdd}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.Users}>
                                <UserPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.UsersEdit}
                        element={
                            <ProtectedElement featureType={DemeterFeatureType.Users}>
                                <UserPage />
                            </ProtectedElement>
                        }
                    />
                    <Route
                        path={routes.UsersImport}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.Users}>
                                    <UsersImportPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.AdministrationMarketIndicators}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.MarketIndicators}>
                                    <MarketIndicatorsMangementPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.AdministrationMarketIndicatorEdit}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.MarketIndicators}>
                                    <MarketIndicatorsPage isInAdministrationMode />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.AdministrationMarketFactorAdd}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.MarketIndicators}>
                                    <MarketIndicatorFactorPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.AdministrationMarketFactorEdit}
                        element={
                            <BusinessOwnerProtectedElement>
                                <ProtectedElement featureType={DemeterFeatureType.MarketIndicators}>
                                    <MarketIndicatorFactorPage />
                                </ProtectedElement>
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.MarketIntelligenceReports}
                        element={
                            <BusinessOwnerProtectedElement>
                                <MarketIntelligenceReportsPage />
                            </BusinessOwnerProtectedElement>
                        }
                    />
                    <Route
                        path={routes.FeatureFlags}
                        element={
                            <AdminstratorProtectedElement>
                                <FeatureFlagsPage />
                            </AdminstratorProtectedElement>
                        }
                    />
                    <Route path={routes.Faqs} element={<FrequentlyAskedQuestionsPage />} />
                    <Route path={routes.Disclaimer} element={<DisclaimerPage />} />
                    <Route path={routes.PrivacyPolicy} element={<PrivacyPolicyPage />} />
                    <Route path={routes.OutlookAndReports} element={<OutlookAndReportsPage />} />
                    <Route path={routes.CancelTrial} element={<CancelTrialPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Route>
            </Routes>
        </Suspense>
    );
};

export default RouterRoutes;
